/**
 * DataLineageModal Component
 *
 * Modal dialog that displays the data lineage (provenance) of a railML file
 * Shows the high-level dataset chain from source systems to the final railML file
 */

import { useRef } from 'react';
import { Modal, Text, Link } from '@bn-apps/design-system-ui-react';
import { Button } from '@bn-apps/dim-ui-react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import routes from '@config/routes';
import LoadingState from '@components/common/LoadingState/LoadingState';
import type { DataLineage } from '@/types';
import { getIntlLocale } from '@utils/locale.utils';
import styles from './DataLineageModal.module.scss';

interface DataLineageModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineage: DataLineage | null;
  loading: boolean;
  fileName: string;
}

function DataLineageModal({ isOpen, onClose, lineage, loading, fileName }: DataLineageModalProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  const formatTimestamp = (date: Date): string => {
    return new Intl.DateTimeFormat(getIntlLocale(i18n.language), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  };

  return (
    <Modal
      isOpen={isOpen}
      openModal={() => {}} // No-op since modal is controlled externally
      closeModal={onClose}
      modalRef={modalRef}
      title={t('files.dataLineage.title')}
      size="md"
      actions={
        <Button hierarchy="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <Text fontSize="md" className="bn-mbe-400">
            {t('files.dataLineage.description', { fileName })}
          </Text>
          <Link asChild>
            <RouterLink
              to={routes.helpDataLineage}
              onClick={onClose}
              className={styles.learnMoreLink}
            >
              {t('files.dataLineage.helpLink')} →
            </RouterLink>
          </Link>
        </div>

        {loading && (
          <LoadingState />
        )}

        {!loading && !lineage && (
          <Text fontSize="sm" className="bn-text-color-subtle">
            {t('files.dataLineage.noData')}
          </Text>
        )}

        {!loading && lineage && (
          <div className={styles.lineageChain}>
            {/* railML File */}
            <div className={`${styles.lineageItem} ${styles.railmlFile}`}>
              <div className={styles.lineageIcon}>📄</div>
              <div className={styles.lineageContent}>
                <div className={styles.lineageHeader}>
                  <Text fontSize="md">
                    {t('files.dataLineage.railmlFile')}
                  </Text>
                </div>
                <dl className={styles.lineageDetails}>
                  <dt><Text fontSize="sm">{t('files.dataLineage.detailTitle')}:</Text></dt>
                  <Text fontSize="sm">{lineage.railmlFile.title}</Text>
                  <dt><Text fontSize="sm">{t('files.dataLineage.creationTime')}:</Text></dt>
                  <dd><Text fontSize="sm">{formatTimestamp(lineage.railmlFile.created)}</Text></dd>
                  {lineage.railmlFile.identifier && (
                    <>
                      <dt><Text fontSize="sm">{t('files.dataLineage.identifier')}:</Text></dt>
                      <dd className={styles.identifier}><Text fontSize="sm">{lineage.railmlFile.identifier}</Text></dd>
                    </>
                  )}
                </dl>
              </div>
            </div>

            {lineage.sourceTtl && (
              <>
                <div className={styles.lineageArrow}>
                  <div className={styles.arrowLine}></div>
                  <span className={styles.arrowIcon}>↑</span>
                </div>

                {/* Source TTL */}
                <div className={`${styles.lineageItem} ${styles.sourceTtl}`}>
                  <div className={styles.lineageIcon}>📋</div>
                  <div className={styles.lineageContent}>
                    <div className={styles.lineageHeader}>
                      <Text fontSize="md">
                        {t('files.dataLineage.sourceTtl')}
                      </Text>
                    </div>
                    <Text fontSize="sm" className="bn-mbe-300 bn-text-weight-bold">
                      {lineage.sourceTtl.title}
                    </Text>
                    <dl className={styles.lineageDetails}>
                      <dt><Text fontSize="sm">{t('files.dataLineage.creationTime')}:</Text></dt>
                      <dd><Text fontSize="sm">{formatTimestamp(lineage.sourceTtl.created)}</Text></dd>
                      {lineage.sourceTtl.identifier && (
                        <>
                          <dt><Text fontSize="sm">{t('files.dataLineage.identifier')}:</Text></dt>
                          <dd className={styles.identifier}><Text fontSize="sm">{lineage.sourceTtl.identifier}</Text></dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              </>
            )}

            {lineage.knowledgeGraph && (
              <>
                <div className={styles.lineageArrow}>
                  <div className={styles.arrowLine}></div>
                  <span className={styles.arrowIcon}>↑</span>
                </div>

                {/* Knowledge Graph */}
                <div className={`${styles.lineageItem} ${styles.knowledgeGraph}`}>
                  <div className={styles.lineageIcon}>🗂️</div>
                  <div className={styles.lineageContent}>
                    <div className={styles.lineageHeader}>
                      <Text fontSize="sm" className="bn-text-weight-semibold bn-text-color-subtle">
                        {t('files.dataLineage.knowledgeGraph')}
                      </Text>
                    </div>
                    <Text fontSize="sm" className="bn-mbe-300 bn-text-weight-bold">
                      {lineage.knowledgeGraph.title}
                    </Text>
                    <dl className={styles.lineageDetails}>
                      <dt><Text fontSize="sm">{t('files.dataLineage.creationTime')}:</Text></dt>
                      <dd><Text fontSize="sm">{formatTimestamp(lineage.knowledgeGraph.created)}</Text></dd>
                      {lineage.knowledgeGraph.identifier && (
                        <>
                          <dt><Text fontSize="sm">{t('files.dataLineage.identifier')}:</Text></dt>
                          <dd className={styles.identifier}><Text fontSize="sm">{lineage.knowledgeGraph.identifier}</Text></dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              </>
            )}

            {lineage.sources.length > 0 && (
              <>
                <div className={styles.lineageArrow}>
                  <div className={styles.arrowLine}></div>
                  <span className={styles.arrowIcon}>↑</span>
                </div>

                {/* Source Systems */}
                <div className={`${styles.lineageItem} ${styles.sourceSystems}`}>
                  <div className={styles.lineageIcon}>💾</div>
                  <div className={styles.lineageContent}>
                    <div className={styles.lineageHeader}>
                      <Text fontSize="sm" className="bn-text-weight-semibold bn-text-color-subtle">
                        {t('files.dataLineage.sourceSystems')}
                      </Text>
                      <Text fontSize="xs" className="bn-text-color-subtle">
                        {lineage.sources.length} {lineage.sources.length === 1 ? t('files.dataLineage.source') : t('files.dataLineage.sources')}
                      </Text>
                    </div>
                    <div className={styles.sourcesGrid}>
                      {lineage.sources.map((source, index) => (
                        <div key={index} className={styles.sourceCard}>
                          <Text fontSize="sm" className="bn-text-weight-semibold bn-mbe-200">
                            {source.title}
                          </Text>
                          <Text fontSize="xs" className="bn-text-color-subtle">
                            {formatTimestamp(source.created)}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="bn-mbs-600">
          <Text fontSize="sm" className="bn-text-color-subtle">
            {t('files.dataLineage.note')}
          </Text>
        </div>
      </div>
    </Modal>
  );
}

export default DataLineageModal;
